import classNames from 'classnames'
import FlexibleLink from 'src/components/ui/FlexibleLink/FlexibleLink'
import type {
  GridCategoriesCardData,
  GridCategoriesProps,
  GridCategoriesTheme,
} from 'src/types/gridCategories2'
import isSafeLink from 'src/utils/isSafeLink'
import ArrowRightLink from 'src/components/Icons/ArrowRightLink'

import CategoryGridCard from './CategoryGridCard'

const CARD_KEY_REGEX = /^card\d+$/

const CARD_POSITION_CLASSES = [
  'lg:col-start-1 lg:col-end-2 lg:row-start-1 lg:row-end-3',
  'lg:col-start-2 lg:col-end-4 lg:row-start-1 lg:row-end-2',
  'lg:col-start-4 lg:col-end-5 lg:row-start-1 lg:row-end-2',
  'lg:col-start-2 lg:col-end-3 lg:row-start-2 lg:row-end-3',
  'lg:col-start-3 lg:col-end-5 lg:row-start-2 lg:row-end-3',
] as const

const CARD_DESKTOP_DIMENSIONS = [
  { width: 344, height: 608 },
  { width: 709, height: 292 },
  { width: 344, height: 292 },
  { width: 344, height: 292 },
  { width: 709, height: 292 },
] as const

const ALLOWED_CARD_KEYS = new Set(['card1', 'card2', 'card3', 'card4', 'card5'])

const isValidCard = (card?: GridCategoriesCardData) => {
  return Boolean(
    card?.title?.trim() &&
      card?.altText?.trim() &&
      card?.desktopImage?.trim() &&
      card?.mobileImage?.trim() &&
      isSafeLink(card?.link)
  )
}

export default function GridCategories20(props: Readonly<GridCategoriesProps>) {
  const {
    theme = 'light',
    title,
    subtitle,
    seeAll,
    card1,
    card2,
    card3,
    card4,
    card5,
  } = props

  const normalizedTheme: GridCategoriesTheme =
    theme === 'dark' ? 'dark' : 'light'

  const parsedCards: GridCategoriesCardData[] = [
    card1,
    card2,
    card3,
    card4,
    card5,
  ]

  const hasUnexpectedCardKey = Object.keys(props).some(
    (key) => CARD_KEY_REGEX.test(key) && !ALLOWED_CARD_KEYS.has(key)
  )

  const showSeeAll = Boolean(seeAll?.title?.trim() && isSafeLink(seeAll?.link))

  if (hasUnexpectedCardKey || !parsedCards.every((card) => isValidCard(card))) {
    return null
  }

  return (
    <section
      data-testid="grid-categories-2-0"
      aria-label="Grid de categorias"
      className={classNames('w-full pt-[50px] pb-[18px] md:pb-0', {
        'bg-darkBgComponent': normalizedTheme === 'dark',
        'bg-white': normalizedTheme !== 'dark',
      })}
    >
      <div className="mx-auto w-full max-w-[1440px]">
        <div className="mb-6 flex flex-col gap-3 lg:mb-8 lg:flex-row lg:items-end lg:justify-between px-4 lg:px-0">
          <div className="flex flex-col gap-2">
            {title && (
              <span
                className={classNames(
                  'text-[27.3px] md:text-[42px] font-bold tracking-[-0.27px] md:tracking-[-0.42px] leading-none',
                  {
                    'text-white': normalizedTheme === 'dark',
                    'text-restructure-neutral-23': normalizedTheme !== 'dark',
                  }
                )}
              >
                {title}
              </span>
            )}

            {subtitle && (
              <span className="text-[15px] md:text-[17px] leading-none lg:max-w-[480px] text-restructure-neutral-24">
                {subtitle}
              </span>
            )}
          </div>

          {showSeeAll && (
            <FlexibleLink
              linkUrl={seeAll?.link ?? ''}
              classNames={classNames(
                'w-fit flex items-center gap-2 text-[15px] font-bold  tracking-[0.08em] transition-opacity hover:opacity-80 mt-1.5 lg:mt-0',
                {
                  'text-primary': normalizedTheme === 'dark',
                  'text-restructure-neutral-14': normalizedTheme !== 'dark',
                }
              )}
            >
              <span className="underline">{seeAll?.title}</span>
              <ArrowRightLink
                color={normalizedTheme === 'dark' ? '#F9D82C' : '#101426'}
              />
            </FlexibleLink>
          )}
        </div>

        <section
          data-testid="grid-categories-2-0-mosaic"
          className="grid grid-cols-1 gap-[18px] lg:grid-cols-4 lg:grid-rows-[292px_292px] lg:gap-[21px]"
        >
          {parsedCards.map((card, index) => (
            <CategoryGridCard
              key={`${card.title}-${index}`}
              card={card}
              cardPositionClass={CARD_POSITION_CLASSES[index]}
              desktopWidth={CARD_DESKTOP_DIMENSIONS[index].width}
              desktopHeight={CARD_DESKTOP_DIMENSIONS[index].height}
            />
          ))}
        </section>
      </div>
    </section>
  )
}
